import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { useGiftSurfaceVisibility } from '../navigation/useGiftSurfaceVisibility.js';
import { SurfaceUnavailableScreen } from './customerEntitlementGuards.js';

export type GiftFlagRequirement = 'donate' | 'recurring' | 'my-donations';

export interface FeatureFlagRouteRequirements {
  readonly master?: boolean;
  readonly anyOf?: readonly GiftFlagRequirement[];
}

interface FeatureFlagRouteGuardProps {
  readonly requires?: FeatureFlagRouteRequirements;
  readonly mode?: 'inline-unavailable' | 'redirect-unavailable';
  readonly children: ReactNode;
}

function resolveRequirementMet(
  gift: ReturnType<typeof useGiftSurfaceVisibility>,
  requires: FeatureFlagRouteRequirements,
): boolean {
  if (requires.master === true && !gift.masterVisible) {
    return false;
  }
  if (requires.anyOf !== undefined && requires.anyOf.length > 0) {
    const matches = requires.anyOf.some((key) => {
      if (key === 'donate') {
        return gift.donateVisible;
      }
      if (key === 'recurring') {
        return gift.recurringVisible;
      }
      return gift.myDonationsVisible;
    });
    if (!matches) {
      return false;
    }
  }
  return true;
}

export function FeatureFlagRouteGuard({
  requires = { master: true },
  mode = 'redirect-unavailable',
  children,
}: FeatureFlagRouteGuardProps): JSX.Element {
  const tenantCode = useTenantCode();
  const gift = useGiftSurfaceVisibility();
  const { t } = useTranslation('customer');

  if (!gift.flagsReady) {
    return (
      <main className="mx-auto flex min-h-[40vh] max-w-lg items-center justify-center p-6">
        <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
          {t('shared.loading')}
        </p>
      </main>
    );
  }

  if (!resolveRequirementMet(gift, requires)) {
    if (mode === 'redirect-unavailable') {
      return <Navigate to={`/${encodeURIComponent(tenantCode)}/unavailable`} replace />;
    }
    return <SurfaceUnavailableScreen />;
  }

  return <>{children}</>;
}
